import React, { useCallback } from 'react'
import { IntegrationCategory, type IntegrationServiceId } from '../../../../../common/integrations'
import { OnboardingIntegrationStep } from '../../../Onboarding/OnboardingIntegrationStep/OnboardingIntegrationStep'
import { CONTACTS_EMPTY_STATE_COPIES } from '../../../copies/onboardingCopies'
import classNames from 'classnames'
import styles from './ContactsEmptyState.module.scss'
import { Capability } from '../../../../../models/Onboarding'
import { useTracking } from '../../../../../apps/professional-app/fa/common/tracking/hooks'
import { GrowthTrackEvent } from '../../../../../apps/professional-app/fa/common/tracking'

export interface ContactsEmptyStateProps {
  displayCategory: IntegrationCategory.CRM | IntegrationCategory.PLANNING
  serviceIds: IntegrationServiceId[]
  connectedWithoutSync: IntegrationServiceId[]
  postConnectRedirectPath: (serviceId: IntegrationServiceId) => string
  onDismiss: () => void
  isDismissing?: boolean
}

export const ContactsEmptyState: React.FC<ContactsEmptyStateProps> = ({
  displayCategory,
  serviceIds,
  connectedWithoutSync,
  postConnectRedirectPath,
  onDismiss,
  isDismissing = false,
}) => {
  const content =
    connectedWithoutSync.length > 0
      ? CONTACTS_EMPTY_STATE_COPIES.withoutSync
      : displayCategory === IntegrationCategory.CRM
        ? CONTACTS_EMPTY_STATE_COPIES.crm
        : CONTACTS_EMPTY_STATE_COPIES.planning

  const { trackingClient } = useTracking()

  const handleIntegrationSelect = useCallback(
    (serviceId: IntegrationServiceId) => {
      trackingClient.track({
        eventName: GrowthTrackEvent.CONTACTS_EMPTY_STATE_INTEGRATION_CLICKED,
        eventDetails: { integrationId: serviceId },
      })
    },
    [trackingClient],
  )

  const dismissButton = (
    <button type='button' className='btn button-medium zocks-text-primary p-0' disabled={isDismissing} onClick={onDismiss}>
      {isDismissing ? 'Saving…' : 'Set up later in integrations'}
    </button>
  )

  return (
    <div className={classNames(styles.container, 'd-flex flex-column align-items-center gap-4')}>
      <OnboardingIntegrationStep
        icon={content.icon}
        headerTitle={content.headerTitle}
        headerSubtitle={content.headerSubtitle}
        serviceIds={serviceIds}
        selectorDisplayMode={'carousel'}
        connectedWithoutSync={connectedWithoutSync}
        postConnectRedirectPath={postConnectRedirectPath}
        secondaryAction={dismissButton}
        capability={Capability.CONTACT_SOURCE}
        onIntegrationSelect={handleIntegrationSelect}
        notificationText={
          displayCategory === IntegrationCategory.PLANNING ? "CRM tools aren't available for your account. Ask your admin which tools are enabled." : undefined
        }
      />
    </div>
  )
}
